import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, UserPlus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { formatApiErrorDetail } from "../lib/apiClient";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "", newsletter: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError("");
    if (form.password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (form.password !== form.confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      await register({ name: form.name.trim(), email: form.email.trim().toLowerCase(), password: form.password, newsletter: form.newsletter });
      toast.success("Account created — pulling in your order history");
      navigate("/account");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not create your account.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-16" data-testid="register-page">
      <span className="eyebrow">Researcher account</span>
      <h1 className="font-display font-extrabold text-4xl mt-3">Create account</h1>
      <p className="text-muted-foreground mt-3">We'll automatically link your past orders by email so your full history is in one place.</p>
      <form onSubmit={submit} className="card-soft rounded-2xl p-6 sm:p-8 mt-8 space-y-5" data-testid="register-form">
        <div>
          <label className="text-sm font-bold" htmlFor="reg-name">Full name</label>
          <Input id="reg-name" required maxLength={120} value={form.name} onChange={set("name")} className="mt-2 h-11 bg-white" data-testid="register-name" />
        </div>
        <div>
          <label className="text-sm font-bold" htmlFor="reg-email">Email</label>
          <Input id="reg-email" type="email" required maxLength={254} value={form.email} onChange={set("email")} className="mt-2 h-11 bg-white" data-testid="register-email" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-bold" htmlFor="reg-password">Password</label>
            <Input id="reg-password" type="password" required minLength={8} value={form.password} onChange={set("password")} className="mt-2 h-11 bg-white" data-testid="register-password" />
          </div>
          <div>
            <label className="text-sm font-bold" htmlFor="reg-confirm">Confirm</label>
            <Input id="reg-confirm" type="password" required value={form.confirm} onChange={set("confirm")} className="mt-2 h-11 bg-white" data-testid="register-confirm" />
          </div>
        </div>
        <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="register-newsletter-label">
          <input type="checkbox" checked={form.newsletter} onChange={(e) => setForm((f) => ({ ...f, newsletter: e.target.checked }))} className="mt-1 accent-[#8a681c] w-4 h-4" data-testid="register-newsletter" />
          <span className="text-sm text-muted-foreground">Email me sales & new-product drops. <span className="font-semibold text-foreground">Get 20% off your first order.</span></span>
        </label>
        {error && <p role="alert" className="text-sm text-destructive" data-testid="register-error">{error}</p>}
        <button type="submit" disabled={busy} className="w-full h-12 rounded-xl btn-ink font-display font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="register-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Create account
        </button>
      </form>
      <p className="text-sm text-muted-foreground mt-6 text-center">
        Already have an account? <Link to="/login" className="font-semibold underline underline-offset-4" style={{ color: "var(--gold)" }} data-testid="register-to-login">Sign in</Link>
      </p>
    </div>
  );
}
